import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp 
} from 'firebase/firestore';

// UGC Analytics Service for analyzing top-performing content
export class UGCAnalyticsService {
  constructor(userId) {
    this.userId = userId;
    this.ugcDataRef = collection(db, 'ugcData');
    this.briefsRef = collection(db, 'briefs');
    this.scriptsRef = collection(db, 'scripts');
    this.creatorsRef = collection(db, 'creators');
    this.performanceRef = collection(db, 'performance');
  }

  // Store UGC data from platforms
  async storeUGCData(platform, ugcData) {
    try {
      const docRef = await addDoc(this.ugcDataRef, {
        userId: this.userId,
        platform,
        ...ugcData,
        createdAt: Timestamp.now()
      });
      return { id: docRef.id, ...ugcData };
    } catch (error) {
      console.error('Error storing UGC data:', error);
      throw error;
    }
  }

  // Get top performing UGC content
  async getTopPerformingUGC(platform, limit = 10, dateRange = 'last_30d') {
    try {
      const q = query(
        this.ugcDataRef,
        where('userId', '==', this.userId),
        where('platform', '==', platform),
        orderBy('performanceScore', 'desc'),
        limit(limit)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching top performing UGC:', error);
      throw error;
    }
  }

  // Analyze UGC performance patterns
  async analyzeUGCPerformance(platform, ugcData) {
    try {
      const analysis = {
        topPerformers: [],
        commonPatterns: [],
        recommendations: [],
        metrics: {
          avgViews: 0,
          avgCTR: 0,
          avgConversion: 0,
          avgEngagement: 0
        }
      };

      if (ugcData && ugcData.length > 0) {
        // Calculate average metrics
        const totalViews = ugcData.reduce((sum, item) => sum + (item.views || 0), 0);
        const totalCTR = ugcData.reduce((sum, item) => sum + (item.ctr || 0), 0);
        const totalConversion = ugcData.reduce((sum, item) => sum + (item.conversion || 0), 0);
        const totalEngagement = ugcData.reduce((sum, item) => sum + (item.engagement || 0), 0);

        analysis.metrics = {
          avgViews: totalViews / ugcData.length,
          avgCTR: totalCTR / ugcData.length,
          avgConversion: totalConversion / ugcData.length,
          avgEngagement: totalEngagement / ugcData.length
        };

        // Find top performers (above average)
        analysis.topPerformers = ugcData.filter(item => 
          item.views > analysis.metrics.avgViews * 1.5 ||
          item.ctr > analysis.metrics.avgCTR * 1.3 ||
          item.conversion > analysis.metrics.avgConversion * 1.5
        );

        // Analyze common patterns
        analysis.commonPatterns = this.analyzeCommonPatterns(ugcData);
        
        // Generate recommendations
        analysis.recommendations = this.generateRecommendations(analysis);
      }

      return analysis;
    } catch (error) {
      console.error('Error analyzing UGC performance:', error);
      throw error;
    }
  }

  // Analyze common patterns in top-performing content
  analyzeCommonPatterns(ugcData) {
    const patterns = {
      hooks: {},
      CTAs: {},
      durations: {},
      creators: {},
      contentTypes: {}
    };

    ugcData.forEach(item => {
      // Analyze hooks
      if (item.hook) {
        patterns.hooks[item.hook] = (patterns.hooks[item.hook] || 0) + 1;
      }

      // Analyze CTAs
      if (item.cta) {
        patterns.CTAs[item.cta] = (patterns.CTAs[item.cta] || 0) + 1;
      }

      // Analyze durations
      if (item.duration) {
        const durationRange = this.getDurationRange(item.duration);
        patterns.durations[durationRange] = (patterns.durations[durationRange] || 0) + 1;
      }

      // Analyze creators
      if (item.creator) {
        patterns.creators[item.creator] = (patterns.creators[item.creator] || 0) + 1;
      }

      // Analyze content types
      if (item.contentType) {
        patterns.contentTypes[item.contentType] = (patterns.contentTypes[item.contentType] || 0) + 1;
      }
    });

    return patterns;
  }

  // Generate recommendations based on analysis
  generateRecommendations(analysis) {
    const recommendations = [];

    if (analysis.topPerformers.length > 0) {
      // Hook recommendations
      const topHooks = Object.entries(analysis.commonPatterns.hooks)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3);
      
      if (topHooks.length > 0) {
        recommendations.push({
          type: 'hook',
          title: 'Top Performing Hooks',
          description: `Use these hook types: ${topHooks.map(([hook]) => hook).join(', ')}`,
          priority: 'high'
        });
      }

      // CTA recommendations
      const topCTAs = Object.entries(analysis.commonPatterns.CTAs)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3);
      
      if (topCTAs.length > 0) {
        recommendations.push({
          type: 'cta',
          title: 'Effective Call-to-Actions',
          description: `Use these CTAs: ${topCTAs.map(([cta]) => cta).join(', ')}`,
          priority: 'high'
        });
      }

      // Duration recommendations
      const topDurations = Object.entries(analysis.commonPatterns.durations)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 2);
      
      if (topDurations.length > 0) {
        recommendations.push({
          type: 'duration',
          title: 'Optimal Content Duration',
          description: `Focus on ${topDurations[0][0]} content for best performance`,
          priority: 'medium'
        });
      }
    }

    return recommendations;
  }

  // Get duration range for analysis
  getDurationRange(duration) {
    if (duration <= 15) return '0-15s';
    if (duration <= 30) return '16-30s';
    if (duration <= 60) return '31-60s';
    return '60s+';
  }

  // Analyze brief performance
  async analyzeBriefPerformance(briefId) {
    try {
      const briefDoc = await getDoc(doc(this.briefsRef, briefId));
      if (!briefDoc.exists()) {
        throw new Error('Brief not found');
      }

      const briefData = briefDoc.data();
      
      // Get all UGC content created from this brief
      const q = query(
        this.ugcDataRef,
        where('userId', '==', this.userId),
        where('briefId', '==', briefId)
      );
      const snapshot = await getDocs(q);
      const ugcFromBrief = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate brief performance metrics
      const totalViews = ugcFromBrief.reduce((sum, item) => sum + (item.views || 0), 0);
      const avgCTR = ugcFromBrief.reduce((sum, item) => sum + (item.ctr || 0), 0) / ugcFromBrief.length;
      const avgConversion = ugcFromBrief.reduce((sum, item) => sum + (item.conversion || 0), 0) / ugcFromBrief.length;
      const avgEngagement = ugcFromBrief.reduce((sum, item) => sum + (item.engagement || 0), 0) / ugcFromBrief.length;

      return {
        briefId,
        briefData,
        metrics: {
          totalViews,
          avgCTR,
          avgConversion,
          avgEngagement,
          contentCount: ugcFromBrief.length
        },
        topContent: ugcFromBrief
          .sort((a, b) => (b.views || 0) - (a.views || 0))
          .slice(0, 5)
      };
    } catch (error) {
      console.error('Error analyzing brief performance:', error);
      throw error;
    }
  }

  // Analyze script performance
  async analyzeScriptPerformance(scriptId) {
    try {
      const scriptDoc = await getDoc(doc(this.scriptsRef, scriptId));
      if (!scriptDoc.exists()) {
        throw new Error('Script not found');
      }

      const scriptData = scriptDoc.data();
      
      // Get all UGC content using this script
      const q = query(
        this.ugcDataRef,
        where('userId', '==', this.userId),
        where('scriptId', '==', scriptId)
      );
      const snapshot = await getDocs(q);
      const ugcFromScript = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate script performance metrics
      const totalViews = ugcFromScript.reduce((sum, item) => sum + (item.views || 0), 0);
      const avgCTR = ugcFromScript.reduce((sum, item) => sum + (item.ctr || 0), 0) / ugcFromScript.length;
      const avgConversion = ugcFromScript.reduce((sum, item) => sum + (item.conversion || 0), 0) / ugcFromScript.length;

      return {
        scriptId,
        scriptData,
        metrics: {
          totalViews,
          avgCTR,
          avgConversion,
          contentCount: ugcFromScript.length
        },
        topContent: ugcFromScript
          .sort((a, b) => (b.views || 0) - (a.views || 0))
          .slice(0, 5)
      };
    } catch (error) {
      console.error('Error analyzing script performance:', error);
      throw error;
    }
  }

  // Analyze creator performance
  async analyzeCreatorPerformance(creatorId) {
    try {
      const creatorDoc = await getDoc(doc(this.creatorsRef, creatorId));
      if (!creatorDoc.exists()) {
        throw new Error('Creator not found');
      }

      const creatorData = creatorDoc.data();
      
      // Get all UGC content from this creator
      const q = query(
        this.ugcDataRef,
        where('userId', '==', this.userId),
        where('creatorId', '==', creatorId)
      );
      const snapshot = await getDocs(q);
      const creatorContent = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate creator performance metrics
      const totalViews = creatorContent.reduce((sum, item) => sum + (item.views || 0), 0);
      const avgCTR = creatorContent.reduce((sum, item) => sum + (item.ctr || 0), 0) / creatorContent.length;
      const avgConversion = creatorContent.reduce((sum, item) => sum + (item.conversion || 0), 0) / creatorContent.length;
      const avgEngagement = creatorContent.reduce((sum, item) => sum + (item.engagement || 0), 0) / creatorContent.length;

      return {
        creatorId,
        creatorData,
        metrics: {
          totalViews,
          avgCTR,
          avgConversion,
          avgEngagement,
          contentCount: creatorContent.length
        },
        topContent: creatorContent
          .sort((a, b) => (b.views || 0) - (a.views || 0))
          .slice(0, 5),
        performanceTrend: this.calculatePerformanceTrend(creatorContent)
      };
    } catch (error) {
      console.error('Error analyzing creator performance:', error);
      throw error;
    }
  }

  // Calculate performance trend over time
  calculatePerformanceTrend(content) {
    if (content.length < 2) return 'insufficient_data';

    const sortedContent = content.sort((a, b) => 
      new Date(a.createdAt?.toDate?.() || a.createdAt) - new Date(b.createdAt?.toDate?.() || b.createdAt)
    );

    const firstHalf = sortedContent.slice(0, Math.floor(sortedContent.length / 2));
    const secondHalf = sortedContent.slice(Math.floor(sortedContent.length / 2));

    const firstAvgViews = firstHalf.reduce((sum, item) => sum + (item.views || 0), 0) / firstHalf.length;
    const secondAvgViews = secondHalf.reduce((sum, item) => sum + (item.views || 0), 0) / secondHalf.length;

    const change = ((secondAvgViews - firstAvgViews) / firstAvgViews) * 100;

    if (change > 10) return 'improving';
    if (change < -10) return 'declining';
    return 'stable';
  }
}

export const ugcAnalyticsService = new UGCAnalyticsService();


